import { AlertTriangle, Ban, Clock, ShieldAlert, Shield } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { Link } from "react-router-dom";

type StrikeBannerProps = {
  strikeCount: number;
  isBanned: boolean;
  banExpiresAt: string | null;
  lastStrikeReason: string | null;
  banReason?: string | null;
};

export function StrikeBanner({ strikeCount, isBanned, banExpiresAt, lastStrikeReason, banReason }: StrikeBannerProps) {
  if (isBanned) {
    const isPermanent = !banExpiresAt;
    const displayReason = banReason || lastStrikeReason || 'Policy violations';
    
    return (
      <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
        <Ban className="h-5 w-5" />
        <AlertTitle className="text-lg font-semibold">
          {isPermanent ? 'Account Permanently Banned' : 'Account Suspended'}
        </AlertTitle>
        <AlertDescription className="space-y-2">
          <p className="font-medium">
            Reason: {displayReason}
          </p>
          <p className="text-sm text-muted-foreground">
            You cannot create NFTs, buy/sell, or request withdrawals while {isPermanent ? 'banned' : 'suspended'}.
          </p>
          {banExpiresAt ? (
            <p className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Clock className="w-4 h-4" />
              Suspension ends: {format(new Date(banExpiresAt), "PPP 'at' p")}
            </p>
          ) : (
            <p className="text-sm font-semibold text-destructive">
              This ban is permanent and cannot be appealed.
            </p>
          )}
          <Link 
            to="/anti-fraud" 
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
          >
            <Shield className="w-3 h-3" />
            View Anti-Fraud Policy
          </Link>
        </AlertDescription>
      </Alert>
    );
  }

  if (strikeCount > 0) {
    return (
      <Alert variant="default" className="border-yellow-500/50 bg-yellow-500/10">
        <AlertTriangle className="h-5 w-5 text-yellow-500" />
        <AlertTitle className="text-yellow-500">Account Warning</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            You have <strong>{strikeCount}</strong> strike{strikeCount !== 1 ? "s" : ""} on your account.
            {strikeCount >= 2 && " One more violation will result in a temporary suspension."}
          </p>
          {lastStrikeReason && (
            <p className="text-sm text-muted-foreground">
              Last reason: {lastStrikeReason}
            </p>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}

// Restricted user warning (Strike 2) - displayed prominently
export function RestrictedUserWarning({ strikes }: { strikes: number }) {
  return (
    <Alert variant="destructive" className="border-orange-500/50 bg-orange-500/10">
      <ShieldAlert className="h-5 w-5 text-orange-500" />
      <AlertTitle className="text-orange-500 font-semibold">⚠️ WARNING: Your account has {strikes} strike(s)</AlertTitle>
      <AlertDescription className="space-y-2">
        <p className="text-foreground">
          We have detected suspicious activity or policy violations on your account.
        </p>
        <p className="font-semibold text-orange-400">
          One more violation will result in a permanent ban and forfeiture of all assets.
        </p>
        <Link 
          to="/anti-fraud" 
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <Shield className="w-3 h-3" />
          Read Anti-Fraud Policy
        </Link>
      </AlertDescription>
    </Alert>
  );
}

export function NFTCreationWarning() {
  return (
    <Alert className="border-border/50 bg-muted/30">
      <ShieldAlert className="h-4 w-4" />
      <AlertTitle className="text-sm font-medium">Compliance Notice</AlertTitle>
      <AlertDescription className="text-xs text-muted-foreground space-y-2">
        <p>⚠️ NFTs are subject to automated anti-fraud monitoring.</p>
        <p>Manipulative behavior may result in loss of visibility or tokens.</p>
        <p className="font-medium text-foreground/70">
          If copyright is infringed in any way, FLOW will not be held responsible. 
          We may provide all available data to authorities for investigations.
        </p>
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border/30 mt-2">
          <Link 
            to="/anti-fraud" 
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
          >
            <Shield className="w-3 h-3" />
            Anti-Fraud Policy
          </Link>
          <span className="text-border">|</span>
          <Link 
            to="/anti-fraud#asset-enforcement" 
            className="inline-flex items-center gap-1 text-xs text-destructive hover:underline font-medium"
          >
            <AlertTriangle className="w-3 h-3" />
            Asset Forfeiture Rules
          </Link>
        </div>
      </AlertDescription>
    </Alert>
  );
}

type AntiFraudCheckboxProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

export function AntiFraudCheckbox({ checked, onCheckedChange }: AntiFraudCheckboxProps) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
      <Checkbox 
        id="anti-fraud-agreement" 
        checked={checked} 
        onCheckedChange={(c) => onCheckedChange(c === true)}
        className="mt-0.5"
      />
      <label 
        htmlFor="anti-fraud-agreement" 
        className="text-sm text-muted-foreground cursor-pointer leading-relaxed"
      >
        I agree to the{" "}
        <Link to="/anti-fraud" className="text-primary hover:underline font-medium" target="_blank">
          Anti-Fraud Policy
        </Link>{" "}
        and automatic enforcement rules. I understand that violations may result in asset forfeiture.
      </label>
    </div>
  );
}
